import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, FileText, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { money } from "../lib/format";
import { StatusPill } from "./DashboardPage";
import { AttachmentsDialog, ACCEPTED_ATTACHMENTS, MAX_ATTACHMENT_BYTES } from "../components/InvoiceAttachments";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";

const todayDate = () => new Date().toISOString().slice(0, 10);

const emptyInvoiceForm = () => ({
  vendor_name: "",
  invoice_number: "",
  amount: "",
  due_date: todayDate(),
  description: "",
  notes: "",
});

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [decision, setDecision] = useState(null);
  const [decisionNote, setDecisionNote] = useState("");
  const { user } = useAuth();
  const isApprover = Boolean(user?.is_approver);

  useEffect(() => {
    api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => setVendors([]));
  }, []);

  const submitDecision = async (status) => {
    try {
      await api.put(`/invoices/${decision.id}/decision`, { status, note: decisionNote });
      toast.success(`Invoice ${status}`);
      setDecision(null);
      setDecisionNote("");
      load();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
  };
  const [form, setForm] = useState(emptyInvoiceForm);
  const [fundSource, setFundSource] = useState("ERR");
  const [pprAmount, setPprAmount] = useState("");
  const [summary, setSummary] = useState({});

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setSummary(data)).catch(() => setSummary({}));
  }, []);

  const amount = parseFloat(form.amount) || 0;
  const pprPortion = fundSource === "PPR" ? amount
    : fundSource === "SPLIT" ? Math.min(parseFloat(pprAmount) || 0, amount) : 0;
  const errPortion = Math.max(amount - pprPortion, 0);

  const load = async () => {
    try {
      const { data } = await api.get("/invoices");
      setInvoices(data);
      const { data: s } = await api.get("/dashboard/summary");
      setSummary(s);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!attachment) return toast.error("Attach the vendor invoice before submitting");
    setSaving(true);
    try {
      const { data } = await api.post("/invoices", {
        ...form, amount, err_amount: errPortion, ppr_amount: pprPortion,
      });
      if (attachment) {
        const fd = new FormData();
        fd.append("file", attachment);
        try {
          await api.post(`/invoices/${data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uploadError) {
          toast.error(`Invoice saved, but the attachment failed: ${formatApiError(uploadError.response?.data?.detail)}`);
        }
      }
      toast.success("Invoice submitted for review");
      setOpen(false);
      setAttachment(null);
      setFundSource("ERR");
      setPprAmount("");
      setForm(emptyInvoiceForm());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const totalSubmitted = invoices.reduce((a, b) => a + b.amount, 0);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((a, b) => a + b.amount, 0);

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 md:py-14" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-2">Invoice ledger</div>
          <h1 className="font-heading font-black text-5xl md:text-6xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
            Vendor invoices
          </h1>
          <p className="mt-3 text-[15px]" style={{ color: "var(--muted)" }}>
            Submit vendor invoices to be paid from your captive earned refund pool.
          </p>
        </div>
        <button
          className="btn-primary flex items-center gap-2 self-start md:self-auto"
          onClick={() => {
            setForm(emptyInvoiceForm());
            setOpen(true);
          }}
          data-testid="btn-new-invoice"
        >
          <Plus size={16} /> New Invoice
        </button>
      </div>

      <div className="border grid-borders grid grid-cols-1 md:grid-cols-3" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <Kpi label="Submitted" value={money(totalSubmitted)} sub={`${invoices.length} total`} />
        <Kpi label="Paid" value={money(totalPaid)} sub={`${invoices.filter(i => i.status === "paid").length} settled`} />
        <Kpi label="In queue" value={money(totalSubmitted - totalPaid)} sub="Pending / approved" />
      </div>

      <div className="mt-8 border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b eyebrow" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="col-span-3">Vendor</div>
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Decision</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Amount</div>
        </div>
        <div className="divide-y max-h-[520px] overflow-y-auto overscroll-contain" style={{ borderColor: "var(--border)" }} data-testid="invoice-list-region">
          {invoices.map((inv) => (
            <motion.div key={inv.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-12 px-6 py-4 items-center text-sm gap-3" data-testid={`invoice-${inv.id}`}>
              <div className="col-span-12 md:col-span-3 font-medium" style={{ color: "var(--ink)" }}>
                <div className="flex items-center gap-2"><FileText size={14} style={{ color: "var(--muted)" }}/> {inv.vendor_name}
                  {inv.historical && (
                    <span className="pill text-[9px]" data-testid={`historical-invoice-${inv.id}`}>
                      Historical payment
                    </span>
                  )}
                  {inv.fund_source && inv.fund_source !== "ERR" && (
                    <span className="pill text-[9px]" data-testid={`fund-pill-${inv.id}`}
                      title={inv.fund_source === "SPLIT" ? `ERR ${money(inv.err_amount)} + PPR ${money(inv.ppr_amount)}` : "Paid from Purchase Power Refunds"}>
                      {inv.fund_source}
                    </span>
                  )}
                  {inv.bank_status === "AWAITING_APPROVAL" && (
                    <span className="pill text-[9px]" style={{ color: "#B45309" }} data-testid={`bank-status-${inv.id}`}
                      title="Submitted to the bank — awaiting approval">
                      Awaiting bank approval
                    </span>
                  )}
                </div>
                {inv.description && <div className="text-xs mt-1 truncate" style={{ color: "var(--muted)" }}>{inv.description}</div>}
              </div>
              <div className="col-span-6 md:col-span-2 font-mono text-xs" style={{ color: "var(--muted)" }}>{inv.invoice_number}</div>
              <div className="col-span-6 md:col-span-2" style={{ color: "var(--muted)" }}>{inv.invoice_date}</div>
              <div className="col-span-6 md:col-span-2 text-xs" style={{ color: "var(--muted)" }}>
                {inv.decided_by ? `by ${inv.decided_by}` : inv.status === "pending" ? "Awaiting approval" : "—"}
              </div>
              <div className="col-span-6 md:col-span-1"><StatusPill status={inv.status} /></div>
              <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-3">
                {!inv.historical && (
                  <button
                    onClick={() => setActiveInvoice(inv)}
                    className="btn-ghost !p-2 flex items-center gap-1 text-xs"
                    aria-label={`Documents for invoice ${inv.invoice_number}`}
                    data-testid={`attachments-${inv.id}`}
                  >
                    <Paperclip size={14} /> {inv.attachment_count || 0}
                  </button>
                )}
                {isApprover && inv.status === "pending" && (
                  <button
                    onClick={() => { setDecision(inv); setDecisionNote(""); }}
                    className="btn-ghost !px-2 !py-1 text-xs"
                    data-testid={`decide-${inv.id}`}
                  >
                    Review
                  </button>
                )}
                <span className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>{money(inv.amount)}</span>
              </div>
            </motion.div>
          ))}
          {!invoices.length && <div className="p-12 text-center text-sm" style={{ color: "var(--muted)" }}>No invoices yet. Click "New invoice" to submit your first.</div>}
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="!w-full sm:!max-w-md !rounded-none border-l overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="new-invoice-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading font-black text-3xl tracking-tight" style={{ color: "var(--ink)" }}>New Invoice</SheetTitle>
            <div className="eyebrow">Submit for captive payment</div>
          </SheetHeader>
          <form onSubmit={submit} className="mt-8 space-y-6">
            <Field label="Vendor" required>
              <Select value={form.vendor_name} onValueChange={(v) => setForm({ ...form, vendor_name: v })}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="inv-vendor">
                  <SelectValue placeholder={vendors.length ? "Choose a vendor" : "No vendors set up yet"} />
                </SelectTrigger>
                <SelectContent className="!rounded-none max-h-72">
                  {vendors.map((v) => <SelectItem key={v.id} value={v.name} className="!rounded-none">{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!vendors.length && (
                <p className="mt-2 text-xs" style={{ color: "var(--muted)" }} data-testid="no-vendors-note">
                  Your program administrator has not added any vendors for your company yet.
                </p>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-6">
              <Field label="Invoice #" required>
                <input className="input-flat" required data-testid="inv-number" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} />
              </Field>
              <Field label="Amount (USD)" required>
                <input className="input-flat" type="number" min="0.01" step="0.01" required data-testid="inv-amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </Field>
            </div>
            {summary.ppr_enabled && (
              <Field label="Pay from" required>
                <div className="flex flex-wrap gap-2" data-testid="fund-source-group">
                  {[["ERR", "ERR funds"], ["PPR", "PPR funds"], ["SPLIT", "Split both"]].map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setFundSource(key)}
                      className="px-4 py-2 text-xs font-bold border transition-colors"
                      style={{
                        borderColor: fundSource === key ? "var(--ink)" : "var(--border)",
                        background: fundSource === key ? "var(--ink)" : "transparent",
                        color: fundSource === key ? "#F7F7F6" : "var(--muted)",
                      }}
                      data-testid={`fund-source-${key.toLowerCase()}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs" style={{ color: "var(--muted)" }} data-testid="fund-balances">
                  ERR available {money(summary.err_available || 0)} · PPR available {money(summary.ppr_available || 0)}
                </p>
                {fundSource === "SPLIT" && (
                  <div className="mt-4">
                    <label className="field-label">PPR portion (USD)</label>
                    <input className="input-flat" type="number" min="0" step="0.01" max={amount || undefined}
                      value={pprAmount} onChange={(e) => setPprAmount(e.target.value)} data-testid="inv-ppr-amount" />
                    <p className="mt-2 text-xs" style={{ color: "var(--muted)" }} data-testid="split-preview">
                      ERR {money(errPortion)} + PPR {money(pprPortion)} = {money(amount)}
                    </p>
                  </div>
                )}
              </Field>
            )}
            <Field label="Invoice due date">
              <input className="input-flat" type="date" data-testid="inv-due-date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
              <p className="mt-2 text-xs font-medium" style={{ color: "#B4472D" }} data-testid="due-upon-receipt-note">
                Due upon receipt
              </p>
            </Field>
            <Field label="Description">
              <textarea className="input-flat resize-none" rows={3} data-testid="inv-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional context or memo" />
            </Field>
            <Field label="Notes">
              <textarea className="input-flat resize-none" rows={2} data-testid="inv-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Anything your approver should know" />
            </Field>
            <Field label="Vendor invoice" required>
              <input
                type="file"
                accept={ACCEPTED_ATTACHMENTS}
                data-testid="inv-attachment"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && file.size > MAX_ATTACHMENT_BYTES) {
                    toast.error("Attachments must be 10 MB or smaller");
                    e.target.value = "";
                    return setAttachment(null);
                  }
                  setAttachment(file);
                }}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-solid file:text-sm file:font-semibold file:bg-transparent"
              />
              <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>PDF, JPG or PNG · up to 10 MB</p>
            </Field>
            <div className="pt-4 flex items-center gap-2">
              <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="submit-invoice">
                {saving ? "Submitting..." : "Submit invoice"}
              </button>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost flex items-center gap-1"><X size={14}/> Cancel</button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={Boolean(decision)} onOpenChange={(isOpen) => !isOpen && setDecision(null)}>
        <DialogContent className="!rounded-none max-w-lg" style={{ borderColor: "var(--border)" }} data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">Review invoice</DialogTitle>
            <DialogDescription>
              {decision?.vendor_name} · {decision?.invoice_number} · {money(decision?.amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <Field label="Note (optional)">
            <textarea
              className="input-flat resize-none"
              rows={3}
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              placeholder="Reason or context for this decision"
              data-testid="decision-note"
            />
          </Field>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={() => submitDecision("rejected")} data-testid="reject-invoice">
              Reject
            </button>
            <button type="button" className="btn-primary" onClick={() => submitDecision("approved")} data-testid="approve-invoice">
              Approve
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentsDialog
        invoice={activeInvoice}
        onClose={() => setActiveInvoice(null)}
        onChanged={load}
      />
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="grid-cell p-8">
      <div className="eyebrow">{label}</div>
      <div className="stat-num text-4xl md:text-5xl mt-3">{value}</div>
      <div className="text-sm mt-2" style={{ color: "var(--muted)" }}>{sub}</div>
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required && " *"}</label>
      {children}
    </div>
  );
}
